// src/components/PaymentConfirmDialog.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, CreditCard, Lock, X, AlertCircle, Tag, ExternalLink, RefreshCw, RotateCcw } from 'lucide-react';
import { SiAlipay, SiWechat } from 'react-icons/si';
import { Reservation } from '../types/reservation';
import api from '../api/axios';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  reservation: Reservation | null;
  faqHref?: string;
}

type Quote = {
  reservation_id: number;
  currency: 'JPY';
  title: string;
  total: number;
  display_text: string;
  display_lines: string[];
  final_total?: number;
  discount_amount?: number;
  applied_coupon?: {
    id: number;
    code: string;
    name?: string | null;
    discount_type?: string | null;
    discount_value?: number | null;
  } | null;
  coupon_message?: string | null;
};

type UserCoupon = {
  id: number;
  code: string;
  name?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
};

const DEFAULT_FAQ = '/faq';

const PaymentConfirmDialog: React.FC<Props> = ({ open, onClose, onConfirm, reservation, faqHref }) => {
  const { t, i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language.toLowerCase().startsWith('zh') ? 'zh' : 'ja';

  const [showGuide, setShowGuide] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  const [submitting, setSubmitting] = useState(false);

  const [quote, setQuote] = useState<Quote | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponList, setCouponList] = useState<UserCoupon[]>([]);
  const [couponInput, setCouponInput] = useState('');
  const [couponApplyMsg, setCouponApplyMsg] = useState<string | null>(null);
  // 法人割引はコードを露出せず友好的なラベルで表示する（裏側は code で適用）
  const [companyBenefit, setCompanyBenefit] = useState<{ code: string; label: string } | null>(null);

  const reservationId = reservation?.id;

  const fmtJPY = (n?: number | null) => {
    if (n == null) return null;
    try {
      return new Intl.NumberFormat('ja-JP').format(n);
    } catch {
      return String(n);
    }
  };

  const baseAmount = quote?.total ?? null;
  const finalAmount = quote?.final_total ?? quote?.total ?? null;
  const discount = quote?.discount_amount ?? 0;

  const fetchQuote = async (couponCode?: string | null) => {
    if (!reservationId) return;
    setLoadingQuote(true);
    try {
      const { data } = await api.get(`/pay/quote/${reservationId}`, {
        params: { coupon_code: couponCode || undefined },
      });
      setQuote(data as Quote);

      if (data?.coupon_message) {
        setCouponApplyMsg(String(data.coupon_message));
      } else if (data?.applied_coupon) {
        setCouponApplyMsg(
          `${data.applied_coupon.code}${data.applied_coupon.name ? `（${data.applied_coupon.name}）` : ''} を適用しました。`
        );
      } else {
        setCouponApplyMsg(null);
      }
    } catch (e: any) {
      setQuote(null);
      setQuoteError(
        (e?.response?.data?.detail as string) ||
        (t('payment.load_failed') as string) ||
        'お支払い金額の取得に失敗しました。'
      );
    } finally {
      setLoadingQuote(false);
    }
  };

  const fetchUserCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const { data } = await api.get<UserCoupon[]>(`/user/coupons`);
      const now = Date.now();
      const usable = (data || []).filter((c) => {
        if (c.is_active === false) return false;
        const s = c.starts_at ? new Date(c.starts_at).getTime() : -Infinity;
        const e = c.ends_at ? new Date(c.ends_at).getTime() : Infinity;
        return now >= s && now <= e;
      });
      setCouponList(usable);
    } catch {
      setCouponList([]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  useEffect(() => {
    if (!open || !reservationId) return;
    fetchUserCoupons();

    // 社員割引クーポンを自動適用
    const autoApply = async () => {
      const pending = (sessionStorage.getItem('pending_coupon_code') || '').trim().toUpperCase();
      if (pending) {
        setCouponInput(pending);
        fetchQuote(pending);
        return;
      }
      setCompanyBenefit(null);
      try {
        const { data } = await api.get('/user/company');
        if (data?.coupons?.length) {
          // is_first_visit = true なら初回(例:30%)、false なら継続(例:20%)
          const targetType = data.is_first_visit ? 'first' : 'followup';
          // 段位(初回/継続)が一致する券のみ自動適用。無ければ何も適用しない。
          const match = data.coupons.find((c: any) => c.coupon_type === targetType);
          if (match) {
            // ★ 法人割引はコードを入力欄に出さず、友好的なラベルで表示して裏側で適用する
            const tierLabel = ({
              employee: lang === 'zh' ? '员工' : '社員',
              family: lang === 'zh' ? '家属' : 'ご家族',
              friend: lang === 'zh' ? '友人' : 'ご友人',
            } as Record<string, string>)[data.member_type] || '';
            const comp = data.company?.name || '';
            const pct = match.discount_value;
            setCompanyBenefit({
              code: (match.code || '').toUpperCase(),
              label: lang === 'zh'
                ? `${comp} ${tierLabel}优惠${pct ? `（${pct}%OFF）` : ''}已自动适用`
                : `${comp} ${tierLabel}優待${pct ? `（${pct}%OFF）` : ''}を適用中`,
            });
            fetchQuote(match.code); // コードは入力欄に出さない（裏側で適用）
            return;
          }
        }
      } catch { /* 法人非所属なら無視 */ }
      fetchQuote(undefined);
    };
    autoApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reservationId]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!submitting) onClose();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!submitting && !loadingQuote && !quoteError) handleConfirm();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    setTimeout(() => dialogRef.current?.focus(), 0);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting, loadingQuote, quoteError]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (submitting) return;
    if (e.target === e.currentTarget) onClose();
  };

  const applyCoupon = async (code?: string) => {
    // 先清除旧提示，避免闪烁残留
    setCouponApplyMsg(null);
    const typed = (code ?? couponInput).trim().toUpperCase();
    // 手入力クーポンは法人割引より優先（ラベルを消す）。手入力が無ければ適用中の
    // 法人割引コードを維持して再見積り（更新ボタン等）。
    if (typed) setCompanyBenefit(null);
    const raw = typed || (companyBenefit?.code ?? '');
    await fetchQuote(raw || undefined);
    // pending には手入力クーポンのみ保存（法人割引コードは入力欄へ露出させない）
    if (typed) {
      sessionStorage.setItem('pending_coupon_code', typed);
    } else {
      sessionStorage.removeItem('pending_coupon_code');
    }
  };

  const currentCouponCode = useMemo(() => {
    const fromApplied = quote?.applied_coupon?.code?.toUpperCase?.();
    const fromInput = (couponInput || '').trim().toUpperCase();
    return (fromApplied || fromInput || '') || undefined;
  }, [quote?.applied_coupon?.code, couponInput]);

  // ... (previous code)

  // Payment Method
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'alipay' | 'wechat_pay'>('card');

  const handleConfirm = async () => {
    if (!reservationId || !quote || loadingQuote || quoteError) return;
    try {
      setSubmitting(true);

      const payload: any = {
        reservation_id: reservationId,
        payment_method: paymentMethod, // Pass selected method
      };
      if (currentCouponCode) payload.coupon_code = currentCouponCode;

      const resp = await api.post('/pay/create-checkout-session', payload);
      const url: string | undefined = resp.data?.checkout_url;


      try {
        await onConfirm?.();
      } catch {
        /* ignore */
      }

      if (!url) throw new Error('no checkout url');
      window.location.href = url;
    } catch (e) {
      console.error('create checkout failed', e);
      setSubmitting(false);
      setQuoteError((t('payment.start_failed') as string) || '決済セッションの作成に失敗しました。');
    }
  };

  const fallbackPrescription = useMemo(() => {
    const raw = (reservation?.prescription_text || '').trim();
    return raw || null;
  }, [reservation?.prescription_text]);

  if (!open || !reservation) return null;

  const faqLink = faqHref || DEFAULT_FAQ;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-[1px]"
      onClick={handleBackdropClick}
      aria-hidden={!open}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={String(t('mypage.confirm_payment_title') || 'お支払いの確認')}
        tabIndex={-1}
        className="w-full max-w-2xl mx-4 rounded-2xl border border-gray-200 bg-white shadow-2xl outline-none max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {t('mypage.confirm_payment_title')}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {/* 刷新报价 */}
            <button
              onClick={() => applyCoupon()}
              disabled={loadingQuote || submitting}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
              title={(t('payment.refresh_amount') as string) || '金額を再取得'}
              aria-label={(t('payment.refresh_amount') as string) || '金額を再取得'}
            >
              <RefreshCw className={`w - 4 h - 4 ${loadingQuote ? 'animate-spin' : ''} `} />
            </button>
            <button
              onClick={onClose}
              disabled={submitting}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              aria-label="Close"
              title={(t('mypage.cancel') as string) || '閉じる'}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <div className="mb-4 flex items-start gap-2 text-sm text-red-600">
            <Lock className="w-4 h-4 mt-0.5" />
            <p className="font-medium">{t('mypage.confirm_payment_message')}</p>
          </div>

          {/* Payment Method */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              {t('payment.method')}
            </p>
            <div className="space-y-2">
              <label
                className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${paymentMethod === 'card' ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' : 'border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={() => setPaymentMethod('card')}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <CreditCard className="w-6 h-6 text-gray-600" />
                <span className="text-sm font-medium flex-1">
                  {t('payment.credit_card')}
                </span>
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${paymentMethod === 'alipay' ? 'border-coral bg-coral/5 ring-1 ring-coral' : 'border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="alipay"
                  checked={paymentMethod === 'alipay'}
                  onChange={() => setPaymentMethod('alipay')}
                  className="w-4 h-4 text-coral border-gray-300 focus:ring-coral"
                />
                <SiAlipay className="w-6 h-6 text-[#1677FF]" />
                <span className="text-sm font-medium flex-1">
                  {t('payment.alipay')}
                </span>
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${paymentMethod === 'wechat_pay' ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' : 'border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="wechat_pay"
                  checked={paymentMethod === 'wechat_pay'}
                  onChange={() => setPaymentMethod('wechat_pay')}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <SiWechat className="w-6 h-6 text-[#07C160]" />
                <span className="text-sm font-medium flex-1">
                  {t('payment.wechat_pay')}
                </span>
              </label>
            </div>

            {/* 支付必读指南 - 挪动到此处 */}
            <div className="mt-3">
              <button
                onClick={(e) => { e.preventDefault(); setShowGuide(true); }}
                className="flex items-center gap-1.5 text-[11px] text-blue-600 hover:text-blue-700 bg-blue-50/50 border border-blue-100/50 px-2.5 py-1 rounded-lg transition-colors w-max font-semibold"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                {lang === 'zh' ? '微信 / 支付宝 支付必读指南' : 'WeChat / Alipay 支払いガイド'}
              </button>
            </div>
          </div>

          {/* Coupon */}
          <div className="rounded-xl border border-orange-200 bg-orange-50/40 p-3 mb-4">
            <p className="text-sm font-semibold text-orange-900 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              {t('mypage.coupon') || 'クーポン'}
            </p>

            {/* 法人割引が適用中なら、コードではなく友好的なラベルで表示 */}
            {companyBenefit && quote?.applied_coupon?.code?.toUpperCase() === companyBenefit.code && (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-800">
                <Tag className="w-4 h-4 flex-shrink-0" />
                <span>🎁 {companyBenefit.label}</span>
              </div>
            )}

            {/* 選択できるクーポンが無いとき(未所持/読込失敗)は、押しても開かない
                「死んだプルダウン」を出さない。手入力欄は常に残すので、法人コード等は
                そちらから適用できる。読込中のみ無効スタイルで表示。 */}
            {(loadingCoupons || couponList.length > 0) && (
              <div className="mt-2 flex items-center gap-2">
                <select
                  disabled={loadingCoupons}
                  className="flex-1 border rounded-lg px-3 py-2 bg-white text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  onChange={(e) => {
                    const val = e.target.value;
                    setCouponInput(val);
                    if (val) applyCoupon(val);
                  }}
                  value={couponInput}
                >
                  <option value="">{t('mypage.coupon_choose') || 'クーポンを選択'}</option>
                  {couponList.map((c) => (
                    <option key={c.id} value={(c.code || '').toUpperCase()}>
                      {(c.code || '').toUpperCase()} {c.name ? `（${c.name}）` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                placeholder={(t('auth.coupon_placeholder') as string) || 'クーポンコードを入力'}
                className="flex-1 border rounded-lg px-3 py-2 bg-white text-sm"
                spellCheck={false}
                autoCorrect="off"
              />
              <button
                onClick={() => applyCoupon()}
                className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50"
              >
                {t('mypage.apply') || '適用'}
              </button>
              {couponInput && (
                <button
                  onClick={() => {
                    setCouponInput('');
                    applyCoupon('');
                  }}
                  className="px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-100"
                >
                  {t('mypage.clear') || '解除'}
                </button>
              )}
            </div>

            {couponApplyMsg && (
              <p className="mt-2 text-xs text-gray-700">{couponApplyMsg}</p>
            )}
          </div>

          {/* 明细 */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              {t('mypage.prescription_summary') || '処方内容'}
            </p>

            {loadingQuote && <p className="text-gray-500 text-sm">…{t('loading') || '読み込み中'}</p>}

            {!loadingQuote && quoteError && (
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <div>
                    <p>{quoteError}</p>
                    {fallbackPrescription && (
                      <p className="mt-1 text-gray-600 whitespace-pre-line">{fallbackPrescription}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => applyCoupon()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('retry') || '再試行'}
                </button>
              </div>
            )}

            {!loadingQuote && !quoteError && (
              <>
                {Array.isArray(quote?.display_lines) && quote!.display_lines.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1 text-[15px] leading-6 text-gray-900">
                    {quote!.display_lines.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                ) : quote?.display_text ? (
                  <pre className="text-[15px] leading-6 text-gray-900 whitespace-pre-line">
                    {quote.display_text}
                  </pre>
                ) : fallbackPrescription ? (
                  <p className="text-gray-700 text-sm whitespace-pre-line">{fallbackPrescription}</p>
                ) : (
                  <p className="text-gray-500 text-sm">{t('mypage.no_prescription')}</p>
                )}
              </>
            )}
          </div>

          {/* 金額/サービス */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">{t('mypage.service')}</p>
              <p className="text-sm font-medium text-gray-900">
                {t(`service.${reservation.service}`, { defaultValue: reservation.service })}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">{t('mypage.amount')}</p>
              {!loadingQuote && !quoteError ? (
                <div className="text-sm font-semibold text-gray-900">
                  {discount > 0 ? (
                    <div className="space-y-0.5">
                      <div className="line-through text-gray-500">{fmtJPY(baseAmount)} 円</div>
                      <div className="text-orange-600 flex items-center gap-1">
                        <span>-{fmtJPY(discount)} 円</span>
                        {quote?.applied_coupon?.name && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-700 text-amber-50 font-semibold whitespace-nowrap">
                            {quote.applied_coupon.name.replace(/.*?(社員|家族|友人)/, '$1')}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-900 text-base">{fmtJPY(finalAmount)} 円</div>
                    </div>
                  ) : (
                    <span>{fmtJPY(baseAmount)} 円</span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">—</p>
              )}
            </div>
          </div>

          {/* セキュリティ説明 */}
          <div className="mt-2 flex items-start gap-2 text-xs text-gray-500">
            <CreditCard className="w-4 h-4 mt-0.5" />
            <p>
              {t('mypage.payment_secure_note') ||
                'お支払いは Stripe の決済ページで安全に処理されます。カード情報は当院に保存されません。'}
            </p>
          </div>

          {/* 注意 + FAQ */}
          <div className="mt-3 text-xs text-red-600">
            <p className="font-semibold">
              {lang === 'ja'
                ? '購入後のキャンセルはできません。詳しい条件は'
                : '购买后无法取消。详细条件请参阅'}
              <a
                href={faqLink}
                target="_blank"
                rel="noreferrer"
                className="underline inline-flex items-center gap-1 ml-1"
              >
                FAQ
                <ExternalLink className="w-3 h-3" />
              </a>
              {lang === 'ja' ? 'をご覧ください。' : '。'}
            </p>
          </div>
        </div>



        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-60"
          >
            {t('mypage.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !!quoteError || loadingQuote || !quote}
            className={`px-5 py-2 text-sm rounded-md text-white flex items-center gap-2 ${submitting ? 'bg-orange-400 cursor-wait' : 'bg-orange-500 hover:bg-orange-600'
              }`}
            aria-disabled={submitting || !!quoteError || loadingQuote || !quote}
          >
            <CreditCard className="w-4 h-4" />
            {submitting ? (t('processing') as string) || '処理中…' : (t('mypage.confirm') as string) || '支払う'}
          </button>
        </div>
      </div>

      {/* Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowGuide(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowGuide(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-blue-500" />
              {lang === 'zh' ? '支付方式指南' : '支払いガイド'}
            </h3>
            
            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                <h4 className="font-bold flex items-center gap-2 text-blue-900 mb-2">
                  <SiAlipay className="w-4 h-4 text-[#1677FF]" />
                  {lang === 'zh' ? '支付宝 (Alipay)' : 'Alipayの場合'}
                </h4>
                <p>
                  {lang === 'zh' 
                    ? '如果您在微信内置浏览器内，弹出支付金额页面后，请点击右上角的「···」，选择「用浏览器打开」。填写邮箱地址并点击下一步，即可自动跳转至支付宝APP完成支付。'
                    : 'WeChatブラウザをご利用の場合、金額が表示されたら右上の「···」をタップし、「ブラウザで開く」を選択してください。メールアドレスを入力後、Alipayアプリが正常に起動します。'}
                </p>
              </div>

              <div className="bg-green-50/50 p-4 rounded-xl border border-green-100">
                <h4 className="font-bold flex items-center gap-2 text-green-900 mb-2">
                  <SiWechat className="w-4 h-4 text-[#09B83E]" />
                  {lang === 'zh' ? '微信支付 (WeChat Pay)' : 'WeChat Payの場合'}
                </h4>
                <p>
                  {lang === 'zh'
                    ? '受限于网关规则，微信支付将生成一个二维码且不支持本机长按识别。您必须借助【另一台设备】使用微信扫码支付；或者将二维码发送到另一台设备上进行扫码。'
                    : 'システムの制限により、WeChat PayはQRコードが表示されます。長押しでの読み取りはできないため、【別の端末】からQRコードをスキャンしてお支払いください。'}
                </p>
                <p className="mt-2 text-xs text-red-500 font-semibold">
                  {lang === 'zh' ? '* 如果您只有一设备，建议改用支付宝或信用卡付款。' : '* 端末が1台のみの場合は、クレジットカードまたはAlipayをご利用ください。'}
                </p>
              </div>
            </div>
            
            <button onClick={() => setShowGuide(false)} className="mt-6 w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-xl transition-colors">
              {lang === 'zh' ? '我知道了' : '確認しました'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentConfirmDialog;
